import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/axios';

// ─── Constants ────────────────────────────────────────────────────
const ADMIN_STAFF_ROLES = [
  'principal', 'superintendent', 'accountant',
  'librarian', 'receptionist', 'lab_attendant',
];

const TABS = [
  { key: 'students', label: 'Students',       icon: 'fa-user-graduate',      color: 'blue'   },
  { key: 'teachers', label: 'Teachers',       icon: 'fa-chalkboard-teacher', color: 'green'  },
  { key: 'support',  label: 'Support Staff',  icon: 'fa-users-cog',          color: 'purple' },
  { key: 'admin',    label: 'Admin Staff',    icon: 'fa-user-tie',           color: 'indigo' },
];

const PRESETS = [
  ['today',     'Today'],
  ['yesterday', 'Yesterday'],
  ['week',      'Last 7 days'],
  ['30days',    'Last 30 days'],
  ['month',     'This Month'],
  ['all',       'All Time'],
];

const todayStr = () => new Date().toISOString().split('T')[0];
const offsetDate = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
};


// ─── Status badge ─────────────────────────────────────────────────
function StatusBadge({ status }) {
  const styles = {
    Present:      'bg-green-100 text-green-800',
    Absent:       'bg-red-100 text-red-800',
    Late:         'bg-yellow-100 text-yellow-800',
    'Half-Day':   'bg-orange-100 text-orange-800',
    Leave:        'bg-blue-100 text-blue-800',
    'Not Marked': 'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}


// ─── Person history drill-down modal ──────────────────────────────
function PersonHistoryModal({ person, onClose }) {
  if (!person) return null;
  const sorted = [...person.records].sort((a, b) => b.date.localeCompare(a.date));
  const rate = person.total ? ((person.present / person.total) * 100).toFixed(1) : '0.0';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
         onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
           onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center p-4 border-b bg-gradient-to-r from-blue-50 to-white">
          <div>
            <h3 className="text-lg font-bold">{person.name}</h3>
            {person.subtitle && <p className="text-xs text-gray-500">{person.subtitle}</p>}
          </div>
          <button onClick={onClose} className="text-2xl text-gray-400 hover:text-gray-700">×</button>
        </div>

        <div className="grid grid-cols-4 gap-3 p-4 border-b bg-gray-50">
          <div className="text-center">
            <p className="text-xs text-green-700 font-medium">Present</p>
            <p className="text-xl font-bold text-green-800">{person.present}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-red-700 font-medium">Absent</p>
            <p className="text-xl font-bold text-red-800">{person.absent}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-yellow-700 font-medium">Late</p>
            <p className="text-xl font-bold text-yellow-800">{person.late}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-blue-700 font-medium">Rate</p>
            <p className="text-xl font-bold text-blue-800">{rate}%</p>
          </div>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          <h4 className="font-semibold text-sm mb-2">
            Full History ({sorted.length} day{sorted.length !== 1 ? 's' : ''})
          </h4>
          <div className="space-y-1">
            {sorted.map(r => (
              <div key={r.id} className="flex items-center justify-between p-2 rounded border bg-white hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <i className="fas fa-calendar-day text-gray-400"></i>
                  <span className="font-medium text-sm">{r.date}</span>
                  {(r.check_in_local || r.check_out_local) && (
                    <span className="text-xs text-gray-500 font-mono">
                      {r.check_in_local || '—'} → {r.check_out_local || '…'}
                      {r.worked_hours != null && ` · ${r.worked_hours}h`}
                    </span>
                  )}
                </div>
                <StatusBadge status={r.status} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════
export default function AttendanceReports() {
  const [tab, setTab] = useState('students');
  const [activePreset, setActivePreset] = useState('today');
  const [dateFrom, setDateFrom] = useState(todayStr());
  const [dateTo, setDateTo] = useState(todayStr());
  const [selectedCampus, setSelectedCampus] = useState('');
  const [selectedClass, setSelectedClass] = useState('all');
  const [search, setSearch] = useState('');
  const [view, setView] = useState('byDay');
  const [drillPerson, setDrillPerson] = useState(null);

  const [loading, setLoading] = useState(true);
  const [studentAttendance, setStudentAttendance] = useState([]);
  const [staffAttendance, setStaffAttendance] = useState([]);
  const [students, setStudents] = useState([]);
  const [users, setUsers] = useState([]);
  const [supportStaff, setSupportStaff] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [classes, setClasses] = useState([]);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sa, sta, st, us, ss, cp, cl] = await Promise.all([
        api.get('/attendance/').catch(() => ({ data: [] })),
        api.get('/staff-attendance/').catch(() => ({ data: [] })),
        api.get('/students/').catch(() => ({ data: [] })),
        api.get('/users/').catch(() => ({ data: [] })),
        api.get('/staff/').catch(() => ({ data: [] })),
        api.get('/campuses/').catch(() => ({ data: [] })),
        api.get('/classes/').catch(() => ({ data: [] })),
      ]);
      setStudentAttendance(sa.data || []);
      setStaffAttendance(sta.data || []);
      setStudents(st.data || []);
      setUsers(us.data || []);
      setSupportStaff(ss.data || []);
      setCampuses(cp.data || []);
      setClasses(cl.data || []);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  // ─── Date presets ───────────────────────────────────────────────
  const applyPreset = (preset) => {
    setActivePreset(preset);
    const today = todayStr();
    switch (preset) {
      case 'today':     setDateFrom(today); setDateTo(today); break;
      case 'yesterday': setDateFrom(offsetDate(-1)); setDateTo(offsetDate(-1)); break;
      case 'week':      setDateFrom(offsetDate(-6)); setDateTo(today); break;
      case '30days':    setDateFrom(offsetDate(-29)); setDateTo(today); break;
      case 'month': {
        const d = new Date();
        const first = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
        setDateFrom(first); setDateTo(today); break;
      }
      case 'all': setDateFrom(''); setDateTo(''); break;
      default: break;
    }
  };

  const inDateRange = (d) => {
    if (!d) return false;
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  };

  // ─── Scoped people ─────────────────────────────────────────────
  const activeCampus = campuses.find(c => c.id === parseInt(selectedCampus));

  const scopedStudents = useMemo(
    () => selectedCampus
      ? students.filter(s => s.campus_name === activeCampus?.name)
      : students,
    [students, selectedCampus, activeCampus]
  );

  const scopedTeachers = useMemo(
    () => selectedCampus
      ? users.filter(u => u.role === 'teacher' && u.campus_name === activeCampus?.name)
      : users.filter(u => u.role === 'teacher'),
    [users, selectedCampus, activeCampus]
  );

  const scopedSupport = useMemo(
    () => selectedCampus
      ? supportStaff.filter(s => s.campus_id === activeCampus?.id)
      : supportStaff,
    [supportStaff, selectedCampus, activeCampus]
  );

  const scopedAdminStaff = useMemo(() => {
    const list = users.filter(u => ADMIN_STAFF_ROLES.includes(u.role));
    return selectedCampus
      ? list.filter(u => u.campus_name === activeCampus?.name)
      : list;
  }, [users, selectedCampus, activeCampus]);

  // ─── Unified records for current tab ──────────────────────────
  const activeRecords = useMemo(() => {
    const searchLower = search.toLowerCase();

    if (tab === 'students') {
      const studentMap = new Map(scopedStudents.map(s => [s.id, s]));
      return studentAttendance
        .filter(r => studentMap.has(r.student_id))
        .filter(r => inDateRange(r.date))
        .filter(r => {
          if (selectedClass === 'all') return true;
          return studentMap.get(r.student_id)?.class_name === selectedClass;
        })
        .filter(r => {
          if (!searchLower) return true;
          const name = studentMap.get(r.student_id)?.student_name || r.student_name || '';
          return name.toLowerCase().includes(searchLower);
        })
        .map(r => {
          const s = studentMap.get(r.student_id);
          return {
            id: r.id,
            person_id: r.student_id,
            person_name: s?.student_name || r.student_name,
            subtitle: `${s?.class_name || ''} · ${s?.campus_name || ''}`,
            date: r.date,
            status: r.status,
            check_in_local: null,
            check_out_local: null,
            worked_hours: null,
          };
        });
    }

    // Staff tabs
    let personTypeFilter;
    if (tab === 'teachers') personTypeFilter = ['teacher'];
    else if (tab === 'support') personTypeFilter = ['staff'];
    else personTypeFilter = ADMIN_STAFF_ROLES;

    const personMap = new Map();
    if (tab === 'teachers') scopedTeachers.forEach(t => personMap.set(t.id, t));
    else if (tab === 'support') scopedSupport.forEach(s => personMap.set(s.id, s));
    else scopedAdminStaff.forEach(u => personMap.set(u.id, u));

    return staffAttendance
      .filter(r => personTypeFilter.includes(r.person_type))
      .filter(r => personMap.has(r.person_id))
      .filter(r => inDateRange(r.date))
      .filter(r => {
        if (!searchLower) return true;
        const p = personMap.get(r.person_id);
        const name = p ? `${p.first_name} ${p.last_name}` : r.person_name;
        return name.toLowerCase().includes(searchLower);
      })
      .map(r => {
        const p = personMap.get(r.person_id);
        const name = p ? `${p.first_name} ${p.last_name}` : r.person_name;
        const subtitle = tab === 'support'
          ? `${p?.designation || ''} · ${campuses.find(c => c.id === p?.campus_id)?.name || ''}`
          : `${p?.role || r.person_type} · ${p?.campus_name || ''}`;
        return {
          id: r.id,
          person_id: r.person_id,
          person_name: name,
          subtitle,
          date: r.date,
          status: r.status,
          check_in_local: r.check_in_local,
          check_out_local: r.check_out_local,
          worked_hours: r.worked_hours,
        };
      });
  }, [
    tab, studentAttendance, staffAttendance, scopedStudents, scopedTeachers,
    scopedSupport, scopedAdminStaff, dateFrom, dateTo, selectedClass, search, campuses,
  ]);

  // ─── By Day view ───────────────────────────────────────────────
  const byDayData = useMemo(() => {
    const byDay = {};
    activeRecords.forEach(r => {
      if (!byDay[r.date]) byDay[r.date] = { date: r.date, present: 0, absent: 0, late: 0, total: 0, records: [] };
      byDay[r.date].total++;
      if (r.status === 'Present') byDay[r.date].present++;
      else if (r.status === 'Absent') byDay[r.date].absent++;
      else if (r.status === 'Late') byDay[r.date].late++;
      byDay[r.date].records.push(r);
    });
    return Object.values(byDay).sort((a, b) => b.date.localeCompare(a.date));
  }, [activeRecords]);

  // ─── By Person view ────────────────────────────────────────────
  const byPersonData = useMemo(() => {
    const byPerson = {};
    activeRecords.forEach(r => {
      const key = r.person_id;
      if (!byPerson[key]) {
        byPerson[key] = {
          id: key, name: r.person_name, subtitle: r.subtitle,
          present: 0, absent: 0, late: 0, total: 0, records: [],
        };
      }
      byPerson[key].total++;
      if (r.status === 'Present') byPerson[key].present++;
      else if (r.status === 'Absent') byPerson[key].absent++;
      else if (r.status === 'Late') byPerson[key].late++;
      byPerson[key].records.push(r);
    });
    return Object.values(byPerson).sort((a, b) => a.name.localeCompare(b.name));
  }, [activeRecords]);

  // ─── Summary ───────────────────────────────────────────────────
  const summary = useMemo(() => {
    const present = activeRecords.filter(r => r.status === 'Present').length;
    const absent  = activeRecords.filter(r => r.status === 'Absent').length;
    const late    = activeRecords.filter(r => r.status === 'Late').length;
    const total   = activeRecords.length;
    const rate    = total ? ((present / total) * 100).toFixed(1) : '0.0';
    return { present, absent, late, total, rate };
  }, [activeRecords]);

  // ─── Export CSV ────────────────────────────────────────────────
  const handleExport = () => {
    if (activeRecords.length === 0) {
      alert('No records to export.');
      return;
    }
    const headers = ['Date', 'Name', 'Details', 'Status', 'Check-In', 'Check-Out', 'Hours'];
    const rows = [...activeRecords]
      .sort((a, b) => b.date.localeCompare(a.date) || a.person_name.localeCompare(b.person_name))
      .map(r => [
        r.date, r.person_name, r.subtitle || '', r.status,
        r.check_in_local || '', r.check_out_local || '',
        r.worked_hours != null ? r.worked_hours : '',
      ]);
    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    // Prepend BOM so Excel detects UTF-8
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_${tab}_${dateFrom || 'all'}_${dateTo || 'all'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─── Render ────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-bold">
          <i className="fas fa-clipboard-check text-blue-500 mr-2"></i>
          Attendance Reports
        </h2>
        <button
          onClick={handleExport}
          disabled={activeRecords.length === 0}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 text-sm"
        >
          <i className="fas fa-file-csv mr-1"></i> Export CSV
        </button>
      </div>

      {/* Tabs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        {TABS.map(t => {
          const isActive = tab === t.key;
          const colors = {
            blue:   'border-blue-500   text-blue-700   bg-blue-50',
            green:  'border-green-500  text-green-700  bg-green-50',
            purple: 'border-purple-500 text-purple-700 bg-purple-50',
            indigo: 'border-indigo-500 text-indigo-700 bg-indigo-50',
          };
          const count =
            t.key === 'students' ? scopedStudents.length
            : t.key === 'teachers' ? scopedTeachers.length
            : t.key === 'support' ? scopedSupport.length
            : scopedAdminStaff.length;
          return (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); setSelectedClass('all'); }}
              className={`p-3 rounded shadow border-l-4 transition text-left ${
                isActive ? `${colors[t.color]} ring-2 ring-gray-300`
                         : 'border-gray-300 bg-white hover:border-gray-500'
              }`}
            >
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <i className={`fas ${t.icon}`}></i> {t.label}
              </p>
              <p className="text-2xl font-bold mt-1">{count}</p>
            </button>
          );
        })}
      </div>

      {/* Date presets + filters */}
      <div className="bg-white p-3 rounded shadow mb-3">
        <div className="flex flex-wrap gap-2 items-center mb-3">
          <span className="text-xs font-semibold text-gray-600 mr-1">Range:</span>
          {PRESETS.map(([key, label]) => (
            <button
              key={key}
              onClick={() => applyPreset(key)}
              className={`px-3 py-1 text-xs rounded border transition ${
                activePreset === key
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
              }`}
            >
              {label}
            </button>
          ))}
          <div className="flex items-center gap-2 ml-2">
            <label className="text-xs text-gray-500">From</label>
            <input
              type="date"
              className="p-1.5 border rounded text-xs"
              value={dateFrom}
              onChange={e => { setDateFrom(e.target.value); setActivePreset('custom'); }}
            />
            <label className="text-xs text-gray-500">To</label>
            <input
              type="date"
              className="p-1.5 border rounded text-xs"
              value={dateTo}
              onChange={e => { setDateTo(e.target.value); setActivePreset('custom'); }}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <select
            className="p-2 border rounded text-sm"
            value={selectedCampus}
            onChange={e => setSelectedCampus(e.target.value)}
          >
            <option value="">All Campuses</option>
            {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          {tab === 'students' && (
            <select
              className="p-2 border rounded text-sm"
              value={selectedClass}
              onChange={e => setSelectedClass(e.target.value)}
            >
              <option value="all">All Classes</option>
              {[...new Set(scopedStudents.map(s => s.class_name).filter(Boolean))].sort().map(cn => (
                <option key={cn} value={cn}>{cn}</option>
              ))}
            </select>
          )}

          <input
            type="text"
            placeholder="Search by name..."
            className="flex-1 min-w-[180px] p-2 border rounded text-sm"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />

          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => setView('byDay')}
              className={`px-3 py-1.5 text-xs font-semibold ${
                view === 'byDay' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'
              }`}
            >
              <i className="fas fa-calendar-day mr-1"></i> By Day
            </button>
            <button
              onClick={() => setView('byPerson')}
              className={`px-3 py-1.5 text-xs font-semibold border-l ${
                view === 'byPerson' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'
              }`}
            >
              <i className="fas fa-users mr-1"></i> By Person
            </button>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <div className="bg-white p-3 rounded shadow border-l-4 border-gray-500">
          <p className="text-xs text-gray-500">Total Records</p>
          <p className="text-xl font-bold text-gray-800">{summary.total}</p>
        </div>
        <div className="bg-green-50 p-3 rounded shadow border-l-4 border-green-500">
          <p className="text-xs text-green-700">Present</p>
          <p className="text-xl font-bold text-green-800">{summary.present}</p>
        </div>
        <div className="bg-red-50 p-3 rounded shadow border-l-4 border-red-500">
          <p className="text-xs text-red-700">Absent</p>
          <p className="text-xl font-bold text-red-800">{summary.absent}</p>
        </div>
        <div className="bg-yellow-50 p-3 rounded shadow border-l-4 border-yellow-500">
          <p className="text-xs text-yellow-700">Late</p>
          <p className="text-xl font-bold text-yellow-800">{summary.late}</p>
        </div>
        <div className="bg-blue-50 p-3 rounded shadow border-l-4 border-blue-500">
          <p className="text-xs text-blue-700">Attendance Rate</p>
          <p className="text-xl font-bold text-blue-800">{summary.rate}%</p>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white p-4 rounded shadow">
        {loading ? (
          <p className="text-gray-500 text-center py-8">Loading…</p>
        ) : activeRecords.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <i className="fas fa-inbox text-4xl mb-3"></i>
            <p>No attendance records for the selected filters.</p>
            <p className="text-xs mt-1">Try changing the date range or campus filter.</p>
          </div>
        ) : view === 'byDay' ? (
          <DayView data={byDayData} tab={tab} />
        ) : (
          <PersonView data={byPersonData} onDrill={(p) => setDrillPerson(p)} />
        )}
      </div>

      {drillPerson && (
        <PersonHistoryModal person={drillPerson} onClose={() => setDrillPerson(null)} />
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Day View — one row per date, expandable
// ═══════════════════════════════════════════════════════════════════
function DayView({ data, tab }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (date) => setExpanded(p => ({ ...p, [date]: !p[date] }));

  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50">
        <tr>
          <th className="p-3 w-8"></th>
          <th className="p-3 text-left font-semibold text-gray-700">Date</th>
          <th className="p-3 text-right font-semibold text-gray-700">Present</th>
          <th className="p-3 text-right font-semibold text-gray-700">Absent</th>
          <th className="p-3 text-right font-semibold text-gray-700">Late</th>
          <th className="p-3 text-right font-semibold text-gray-700">Total</th>
          <th className="p-3 text-right font-semibold text-gray-700">Rate</th>
        </tr>
      </thead>
      <tbody>
        {data.map(d => {
          const rate = d.total ? ((d.present / d.total) * 100).toFixed(0) : '0';
          const rateColor = rate >= 90 ? 'text-green-700' : rate >= 75 ? 'text-yellow-700' : 'text-red-700';
          return (
            <React.Fragment key={d.date}>
              <tr className="border-t hover:bg-gray-50 cursor-pointer" onClick={() => toggle(d.date)}>
                <td className="p-3 text-gray-400">
                  <i className={`fas fa-chevron-${expanded[d.date] ? 'down' : 'right'} text-xs`}></i>
                </td>
                <td className="p-3 font-medium">{d.date}</td>
                <td className="p-3 text-right text-green-700 font-semibold">{d.present}</td>
                <td className="p-3 text-right text-red-700 font-semibold">{d.absent}</td>
                <td className="p-3 text-right text-yellow-700 font-semibold">{d.late}</td>
                <td className="p-3 text-right text-gray-700">{d.total}</td>
                <td className={`p-3 text-right font-bold ${rateColor}`}>{rate}%</td>
              </tr>
              {expanded[d.date] && (
                <tr>
                  <td colSpan={7} className="bg-gray-50 p-4">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-gray-500">
                          <th className="text-left p-2">Name</th>
                          <th className="text-left p-2">Details</th>
                          {tab !== 'students' && <th className="text-left p-2">Check-In</th>}
                          {tab !== 'students' && <th className="text-left p-2">Check-Out</th>}
                          {tab !== 'students' && <th className="text-left p-2">Hours</th>}
                          <th className="text-left p-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...d.records]
                          .sort((a, b) => a.person_name.localeCompare(b.person_name))
                          .map(r => (
                            <tr key={r.id} className="border-t border-gray-200">
                              <td className="p-2 font-medium">{r.person_name}</td>
                              <td className="p-2 text-gray-500">{r.subtitle}</td>
                              {tab !== 'students' && <td className="p-2 font-mono text-gray-600">{r.check_in_local || '—'}</td>}
                              {tab !== 'students' && <td className="p-2 font-mono text-gray-600">{r.check_out_local || '—'}</td>}
                              {tab !== 'students' && <td className="p-2">{r.worked_hours != null ? `${r.worked_hours}h` : '—'}</td>}
                              <td className="p-2"><StatusBadge status={r.status} /></td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Person View — one row per person, click to drill into history
// ═══════════════════════════════════════════════════════════════════
function PersonView({ data, onDrill }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50">
        <tr>
          <th className="p-3 text-left font-semibold text-gray-700">Name</th>
          <th className="p-3 text-left font-semibold text-gray-700">Details</th>
          <th className="p-3 text-right font-semibold text-gray-700">Present</th>
          <th className="p-3 text-right font-semibold text-gray-700">Absent</th>
          <th className="p-3 text-right font-semibold text-gray-700">Late</th>
          <th className="p-3 text-right font-semibold text-gray-700">Total Days</th>
          <th className="p-3 text-right font-semibold text-gray-700">Rate</th>
          <th className="p-3"></th>
        </tr>
      </thead>
      <tbody>
        {data.map(p => {
          const rate = p.total ? ((p.present / p.total) * 100).toFixed(0) : '0';
          const rateColor = rate >= 90 ? 'text-green-700' : rate >= 75 ? 'text-yellow-700' : 'text-red-700';
          return (
            <tr key={p.id} className="border-t hover:bg-gray-50">
              <td className="p-3 font-medium">{p.name}</td>
              <td className="p-3 text-xs text-gray-500">{p.subtitle}</td>
              <td className="p-3 text-right text-green-700 font-semibold">{p.present}</td>
              <td className="p-3 text-right text-red-700 font-semibold">{p.absent}</td>
              <td className="p-3 text-right text-yellow-700 font-semibold">{p.late}</td>
              <td className="p-3 text-right text-gray-700">{p.total}</td>
              <td className={`p-3 text-right font-bold ${rateColor}`}>{rate}%</td>
              <td className="p-3 text-right">
                <button
                  onClick={() => onDrill(p)}
                  className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                >
                  <i className="fas fa-history mr-1"></i> View History
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}